import { memo, useCallback, useEffect, useMemo, useState } from 'react'

import { generatePath } from 'react-router-dom'
import { push } from 'connected-react-router'

import { KeyboardArrowRightOutlined } from '@mui/icons-material'
import { GridColDef } from '@mui/x-data-grid-pro'
import { Body2, DateInfo, DataGridV2, PageHeader, PageLayout, Stack, usePagination } from '@collaborate-health/uikit'

import Routes from '@tabeeb/routes'
import { useDispatch } from '@tabeeb/store'
import { useAnnouncements } from '@tabeeb/modules/articles/hooks'
import { AnnouncementBaseResponse, AnnouncementSeverity } from '@tabeeb/contracts/microservices/swagger-article-service'

import {
  AnnouncementCreateButton,
  AnnouncementPlatformChip,
  AnnouncementScopeChip,
  AnnouncementSeverityChip,
  ArticlePublicationStatusChip,
} from '@tabeeb/modules/articles/components'
import AnnouncementMenu from '@tabeeb/modules/articles/components/AnnouncementMenu'

const AnnouncementsPage: React.FC = () => {
  const dispatch = useDispatch()

  const [announcementsCount, setAnnouncementsCount] = useState(0)

  const {
    current: currentPage,
    pages,
    size,
    onPageNumberChange,
    onPageSizeChange,
  } = usePagination({ total: announcementsCount })
  const {
    announcements,
    total,
    loading,
    onDeleteAnnouncement,
    onUpdateAnnouncementStatus,
    onUpdateAnnouncementSeverity,
  } = useAnnouncements({
    skip: (currentPage - 1) * size,
    take: size,
  })

  useEffect(() => {
    setAnnouncementsCount(total)
  }, [total])

  const onEdit = useCallback(
    (announcement) => {
      dispatch(push(generatePath(Routes.announcementEdit, { id: announcement.Id })))
    },
    [dispatch]
  )

  const onUpdateSeverity = useCallback(
    (id) => (severity: AnnouncementSeverity) => {
      onUpdateAnnouncementSeverity({ id, severity })
    },
    [onUpdateAnnouncementSeverity]
  )

  const columns = useMemo(
    (): GridColDef<AnnouncementBaseResponse>[] => [
      {
        field: 'Name',
        headerName: 'Name',
        flex: 1,
        minWidth: 150,
        maxWidth: 450,
        renderCell: ({ row }) => (
          <Body2 fontWeight={500} noWrap>
            {row.Name}
          </Body2>
        ),
      },
      {
        field: 'Scope',
        headerName: 'Scope',
        sortable: false,
        width: 125,
        renderCell: ({ row }) => <AnnouncementScopeChip scope={row.Scope} />,
      },
      {
        field: 'Status',
        headerName: 'Status',
        sortable: false,
        width: 140,
        renderCell: ({ row }) => <ArticlePublicationStatusChip status={row.Status} />,
      },
      {
        field: 'Severity',
        headerName: 'Severity',
        sortable: false,
        width: 175,
        renderCell: ({ row }) => (
          <AnnouncementSeverityChip severity={row.Severity} onChange={onUpdateSeverity(row.Id)} />
        ),
      },
      {
        field: 'Platform',
        headerName: 'Platform',
        sortable: false,
        width: 125,
        renderCell: ({ row }) => <AnnouncementPlatformChip platform={row.Platform} />,
      },
      {
        field: 'CreatedOn',
        headerName: 'Created on',
        sortable: false,
        width: 120,
        renderCell: ({ row }) => <DateInfo date={row.CreatedOn} />,
      },
      {
        field: 'UpdatedOn',
        headerName: 'Updated on',
        sortable: false,
        width: 120,
        renderCell: ({ row }) => <DateInfo date={row.UpdatedOn} />,
      },
      {
        field: 'DisplayPeriod',
        headerName: 'Display period',
        sortable: false,
        width: 250,
        renderCell: ({ row }) => (
          <Stack direction='row' alignItems='center' spacing={1}>
            <DateInfo date={row.StartDate} />
            <KeyboardArrowRightOutlined fontSize='small' />
            <DateInfo date={row.ExpiryDate} />
          </Stack>
        ),
      },
      {
        field: 'Actions',
        headerName: '',
        sortable: false,
        width: 50,
        align: 'right',
        renderCell: ({ row }) => (
          <AnnouncementMenu
            announcement={row}
            onDelete={({ id }) => onDeleteAnnouncement(id)}
            onUpdateStatus={onUpdateAnnouncementStatus}
          />
        ),
      },
    ],
    [onDeleteAnnouncement, onUpdateAnnouncementStatus, onUpdateSeverity]
  )

  return (
    <PageLayout background='default' maxWidth='xl' spacing='page'>
      <PageHeader title='Announcements' actions={<AnnouncementCreateButton />} />
      <DataGridV2
        columns={columns}
        rows={announcements}
        getRowId={(row) => row.Id}
        loading={loading}
        rowCount={announcementsCount}
        paginationState={{
          current: currentPage,
          pages,
          pageSize: size,
          onPageNumberChange,
          onPageSizeChange,
        }}
        onRowDoubleClick={(params) => onEdit(params.row)}
      />
    </PageLayout>
  )
}

export default memo(AnnouncementsPage)

